import { useQuery } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { calculateOffset } from 'utils/helpers/pagination'
import { getColumns } from './utils'
import DataTable from 'components/common/dataTable'
import { Button, DataTablePagination, Dropdown } from 'components/common'
import { ExportIcon } from 'components/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  downloadCatalogueActionLogs,
  fetchCatalogueActionLogs,
} from 'utils/api/logsApi'
import { filterEmptyValues } from 'utils/helpers'

const CatalogueActionLogTable = () => {
  const [logResponse, setLogResponse] = useState<any>({})
  const [page, setPage] = useState<number>(0)
  const [availableOperationTypes, setAvailableOperationTypes] = useState<
    string[]
  >([])
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([])

  const { getQuery } = useQuery()
  const { search } = useLocation()
  const navigate = useNavigate()

  const columns = useMemo(() => getColumns(page), [page])

  const getLogs = async () => {
    try {
      const { data } = await fetchCatalogueActionLogs({
        limit: getQuery('limit') || 10,
        page: getQuery('page') || 1,
        operationType: getQuery('operationType') || undefined,
        status: getQuery('status') || undefined,
        startDate: getQuery('startDate') || undefined,
        endDate: getQuery('endDate') || undefined,
      })

      setPage(calculateOffset(data))
      setLogResponse(data)
      setAvailableOperationTypes(data.availableOperationTypes || [])
      setAvailableStatuses(data.availableStatuses || [])
    } catch (error) {
      setLogResponse({ docs: [] })
      setAvailableOperationTypes([])
      setAvailableStatuses([])
    }
  }

  useEffect(() => {
    getLogs()
  }, [search])

  /* ---------------- OPERATION TYPE OPTIONS ---------------- */

  const operationTypeOptions = useMemo(() => {
    const actualOptions = availableOperationTypes.map((type) => ({
      label: type,
      value: type,
    }))

    return [{ label: '-- NONE --', value: '' }, ...actualOptions]
  }, [availableOperationTypes])

  /* ---------------- STATUS OPTIONS ---------------- */

  const statusOptions = useMemo(() => {
    const actualOptions = availableStatuses.map((status) => ({
      label: status,
      value: status,
    }))

    return [{ label: '-- NONE --', value: '' }, ...actualOptions]
  }, [availableStatuses])

  /* ---------------- QUERY PARAM HANDLING ---------------- */

  const updateQueryParam = (key: string, value: string) => {
    const params = new URLSearchParams(window.location.search)

    if (!value) {
      params.delete(key)
    } else {
      params.set(key, value)
    }

    params.set('page', '1')

    navigate({
      pathname: '/catalogue-action-logs',
      search: params.toString(),
    })
  }

  const handleOperationTypeChange = (_fieldName: string, value: string) => {
    updateQueryParam('operationType', value)
  }

  const handleStatusChange = (_fieldName: string, value: string) => {
    updateQueryParam('status', value)
  }

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateQueryParam('startDate', event.target.value)
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateQueryParam('endDate', event.target.value)
  }

  const clearFilter = () => {
    navigate('/catalogue-action-logs')
  }

  const handleExport = async () => {
    const loadingToast = toast.loading('Loading..')
    try {
      const logFilter = filterEmptyValues({
        operationType: getQuery('operationType'),
        status: getQuery('status'),
        startDate: getQuery('startDate'),
        endDate: getQuery('endDate'),
      })

      await downloadCatalogueActionLogs(
        Object.keys(logFilter).length > 0 ? logFilter : undefined,
      )
      toast.success('File will be sent to you in email!', { id: loadingToast })
    } catch (error) {
      toast.error('File Downloading Failed.', { id: loadingToast })
    }
  }

  const hasActiveFilter =
    getQuery('operationType') ||
    getQuery('status') ||
    getQuery('startDate') ||
    getQuery('endDate')

  return (
    <>
      <div className="mb-6 flex items-center justify-between gap-2">
        <h2 className="text-2xl font-semibold text-gray-800">
          Catalogue Action Logs
        </h2>

        <Button
          type="button"
          buttonText="Export"
          variant="custom"
          iconClassName="!h-5 !w-5 text-dataTableIcon"
          icon={<ExportIcon />}
          className="rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200"
          onClick={handleExport}
        />
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-6 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
        <div className="min-w-[220px]">
          <Dropdown
            options={operationTypeOptions}
            name="operationType"
            label="Operation Type"
            defaultValue={getQuery('operationType') || ''}
            setValue={handleOperationTypeChange}
          />
        </div>

        <div className="min-w-[220px]">
          <Dropdown
            options={statusOptions}
            name="status"
            label="Status"
            defaultValue={getQuery('status') || ''}
            setValue={handleStatusChange}
          />
        </div>

        <div className="min-w-[170px]">
          <label className="mb-1 block text-xs font-semibold text-sidebar-primary">
            Start Date
          </label>
          <input
            type="date"
            value={getQuery('startDate') || ''}
            onChange={handleStartDateChange}
            className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-primary focus:border-2 focus:border-gray-500 focus:outline-none"
          />
        </div>

        <div className="min-w-[170px]">
          <label className="mb-1 block text-xs font-semibold text-sidebar-primary">
            End Date
          </label>
          <input
            type="date"
            value={getQuery('endDate') || ''}
            onChange={handleEndDateChange}
            className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-primary focus:border-2 focus:border-gray-500 focus:outline-none"
          />
        </div>

        {hasActiveFilter && (
          <button
            onClick={clearFilter}
            className="mb-[2px] rounded-md border-2 p-3 text-sm font-medium text-gray-500 transition-colors duration-200 hover:text-gray-900"
          >
            Clear
          </button>
        )}
      </div>

      <DataTable columns={columns} rows={logResponse?.docs} identifier="_id" />

      <DataTablePagination
        currentPage={logResponse?.page}
        totalPages={logResponse?.totalPages}
        hasNextPage={logResponse?.hasNextPage}
        hasPrevPage={logResponse?.hasPrevPage}
        limit={logResponse?.limit}
        nextPage={logResponse?.nextPage}
        prevPage={logResponse?.prevPage}
      />
    </>
  )
}

export default CatalogueActionLogTable
